import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import { checkExistingActivity, checkTutorialById } from '../utils/activityService';
import { auth } from '../firebase';
import { formatActivityDate } from '../utils/date';
import { artStyles } from '../data/artStyles';
import { resolveImageUrl, getBackendBaseUrl } from '../utils/urlResolver';
import { getCache, setCache, createCacheKey } from '../utils/cache';
import ArtformDetailAccordions from './ArtformDetailAccordions';
import PremiumActivityModalShell from './PremiumActivityModalShell';
import { coLearningInkMuted, coLearningInkTitle } from '../ui/coLearningModalTheme';

// Helper function to get full image URL (moved outside component for stability)
const getFullImageUrl = (url) => {
  if (!url) return '';
  
  // Data URLs (base64) should work as-is
  if (url.startsWith('data:image/')) {
    return url;
  }
  
  // Blob URLs should work as-is (they're local to the browser)
  // But they might expire, so we'll handle errors in onError
  if (url.startsWith('blob:')) {
    return url;
  }
  
  // Check if it's a BFL URL that needs proxying
  if (url.includes('bfl.ai') || url.includes('delivery-eu4.bfl.ai')) {
    const backendBase = getBackendBaseUrl();
    return `${backendBase}/proxy-image?url=${encodeURIComponent(url)}`;
  }
  
  // Use centralized URL resolver to handle localhost:3001 URLs
  return resolveImageUrl(url);
};

// Helper function to get expanded image URL (moved outside component for stability)
const getExpandedImage = (activity) => {
  if (!activity) return '';
  
  console.log('🔍 getExpandedImage called with activity:', activity);
  
  // Check all possible image URL fields
  const possibleUrls = [
    activity?.transformedImageUrl,
    activity?.imageUrl,
    activity?.result?.imageUrl,
    activity?.metadata?.transformedImageUrl,
    activity?.metadata?.imageUrl,
    activity?.data?.imageUrl,
    activity?.transformedImage,
    activity?.resultImageUrl
  ];
  
  // Check for journey steps first
  if (activity.journey && activity.journey.length > 0) {
    const lastStep = activity.journey[activity.journey.length - 1];
    if (lastStep.transformedImageUrl) {
      console.log('🎨 Found image in journey step:', lastStep.transformedImageUrl);
      return getFullImageUrl(lastStep.transformedImageUrl);
    }
  }
  
  // Check main transformed image
  if (activity.transformedImageUrl) {
    console.log('🎨 Found main transformed image:', activity.transformedImageUrl);
    return getFullImageUrl(activity.transformedImageUrl);
  }
  
  // Check other possible fields
  for (const url of possibleUrls) {
    if (url && typeof url === 'string' && url.length > 0) {
      console.log('🎨 Found image in other field:', url);
      return getFullImageUrl(url);
    }
  }
  
  console.log('❌ No image URL found in activity:', {
    transformedImageUrl: activity.transformedImageUrl,
    imageUrl: activity.imageUrl,
    hasJourney: !!activity.journey,
    journeyLength: activity.journey?.length || 0
  });
  
  return '';
};

const ReimagineScrollerCard = ({
  originalArtworkUrl,
  originalArtworkTitle,
  onTutorialClick,
  onCardClick,
  onDeleteActivity,
  waysOfSeeingActivities = [] // Receive activities as prop
}) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [hasTutorial, setHasTutorial] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showExpandedView, setShowExpandedView] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isFadingExpanded, setIsFadingExpanded] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Clear all modal states when they close
  useEffect(() => {
    if (!showDetailsModal && !expanded) {
      setIsFadingOut(false);
      setIsFadingExpanded(false);
    }
  }, [showDetailsModal, expanded]);

  // Only adjust index when the activity *set* changes (ids), not on every parent re-render
  const waysOfSeeingActivitiesKey = useMemo(
    () => (waysOfSeeingActivities || []).map((a) => a?.id || '').filter(Boolean).join('|'),
    [waysOfSeeingActivities]
  );
  const prevWaysActivitiesKeyRef = useRef(waysOfSeeingActivitiesKey);

  useEffect(() => {
    if (prevWaysActivitiesKeyRef.current === waysOfSeeingActivitiesKey) return;
    prevWaysActivitiesKeyRef.current = waysOfSeeingActivitiesKey;
    const len = waysOfSeeingActivities?.length || 0;
    setCurrentIndex((prev) => (len === 0 ? 0 : Math.min(prev, len - 1)));
  }, [waysOfSeeingActivitiesKey, waysOfSeeingActivities]);

  const currentActivity = waysOfSeeingActivities?.[currentIndex];

  useEffect(() => {
    if (currentActivity && user) {
      checkTutorialExists();
    }
  }, [currentActivity, user]);

  useEffect(() => {
    console.log('🎯 hasTutorial state changed:', hasTutorial, 'for style:', currentActivity?.styleInfo?.name);
  }, [hasTutorial, currentActivity?.styleInfo?.name]);

  useEffect(() => {
    console.log('🎨 Expanded state changed:', expanded);
  }, [expanded]);

  // Close other dashboard modals when *this* card opens its details layer (not when only expanded —
  // expanded is opened from inside that flow and should not re-dispatch or fight parent state).
  useEffect(() => {
    if (showDetailsModal) {
      try {
        window.dispatchEvent(new Event('close-global-details-modal'));
      } catch (err) {}
    }
  }, [showDetailsModal]);

  // Activities are now passed as props, no need to fetch

    // Generate tutorial ID (same as in activityService)
  const generateTutorialId = (styleId, artworkId) => {
    return `tutorial_${styleId}_${artworkId}`;
  };

  const checkTutorialExists = async () => {
    if (!currentActivity || !user) {
      console.log('❌ Cannot check tutorial: missing currentActivity or user');
      setHasTutorial(false);
      return;
    }
    
    // Check if we have the required data
    if (!currentActivity.styleInfo?.id || !currentActivity.id) {
      console.log('❌ Cannot check tutorial: missing styleId or activityId', {
        hasStyleId: !!currentActivity.styleInfo?.id,
        hasActivityId: !!currentActivity.id,
        styleInfo: currentActivity.styleInfo
      });
      setHasTutorial(false);
      return;
    }
    
    try {
    console.log('🔍 Checking tutorial existence for:', {
      style: currentActivity.styleInfo?.name,
      styleId: currentActivity.styleInfo?.id,
        activityId: currentActivity.id,
      userId: user.uid
    });
    
      // Check cache first for tutorial
      const tutorialCacheKey = createCacheKey('tutorial', { 
        styleId: currentActivity.styleInfo.id, 
        artworkId: currentActivity.id 
      }, user?.uid);
      const cachedTutorial = getCache(tutorialCacheKey);
      
      if (cachedTutorial !== null) {
        // Cache hit - null means no tutorial exists, object means tutorial exists
        const hasTutorial = !!cachedTutorial;
        console.log('✅ [REIMAGINE] Using cached tutorial check:', hasTutorial);
        setHasTutorial(hasTutorial);
        if (cachedTutorial) {
          console.log('✅ Found cached tutorial:', {
            id: cachedTutorial.id,
            tutorialId: cachedTutorial.tutorialId,
            styleInfo: cachedTutorial.styleInfo
          });
        }
        return;
      }
      
      // Use checkTutorialById to check for existing tutorial
      const existingTutorial = await checkTutorialById(
        currentActivity.styleInfo.id,
        currentActivity.id
      );
      
      // Cache the result (5 min cache)
      setCache(tutorialCacheKey, existingTutorial, 5 * 60 * 1000);
      console.log('✅ [REIMAGINE] Tutorial check cached');
      
      const hasTutorial = !!existingTutorial;
      console.log('🔍 Tutorial existence check result:', hasTutorial, existingTutorial ? `(ID: ${existingTutorial.id})` : '');
      
      setHasTutorial(hasTutorial);
      
      if (existingTutorial) {
        console.log('✅ Found existing tutorial:', {
          id: existingTutorial.id,
          tutorialId: existingTutorial.tutorialId,
          styleInfo: existingTutorial.styleInfo
        });
      }
    } catch (error) {
      console.error('❌ Error checking tutorial existence:', error);
      setHasTutorial(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => {
      const newIndex = prev > 0 ? prev - 1 : waysOfSeeingActivities.length - 1;
      // Check tutorial existence for the new activity after state update
      setTimeout(() => {
        if (waysOfSeeingActivities[newIndex]) {
          checkTutorialExists();
        }
      }, 0);
      return newIndex;
    });
  };

  const handleNext = () => {
    setCurrentIndex(prev => {
      const newIndex = prev < waysOfSeeingActivities.length - 1 ? prev + 1 : 0;
      // Check tutorial existence for the new activity after state update
      setTimeout(() => {
        if (waysOfSeeingActivities[newIndex]) {
          checkTutorialExists();
        }
      }, 0);
      return newIndex;
    });
  };

  // Memoize the expanded image URL to avoid repeated calls
  // Must be called before any early returns (React Hooks rule)
  const expandedImageUrl = useMemo(() => {
    if (!currentActivity) return '';
    return getExpandedImage(currentActivity);
  }, [currentActivity]);

  // Style row from the activity (used for labels + accordion fallbacks).
  const mergedStyle = useMemo(() => {
    if (!currentActivity) return {};
    const s = currentActivity?.styleInfo || {};
    return { ...s };
  }, [currentActivity]);

  /** Same shape as ArtStyleSelectionPage — merged with Firestore artforms inside accordions. */
  const scrollerAccordionStyleMeta = useMemo(() => {
    if (!currentActivity?.styleInfo) return null;
    const s = currentActivity.styleInfo;
    const fam =
      Array.isArray(s.famousArtists) && s.famousArtists.length
        ? s.famousArtists
        : s.famousArtist
          ? [s.famousArtist]
          : [];
    return {
      name: s.name,
      description: s.description,
      kidFriendlyDescription: s.kidFriendlyDescription,
      characteristics: s.characteristics || [],
      famousArtists: fam,
      yearRange: s.yearRange || s.timePeriod,
    };
  }, [currentActivity]);

  const closeDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setExpanded(false);
    setIsFadingOut(false);
    setIsFadingExpanded(false);
  }, []);

  const openStyleLearnView = useCallback(() => {
    setShowDetailsModal(false);
    setIsFadingOut(false);
    setExpanded(true);
  }, []);

  // No loading state needed since activities are passed as props
  // Early return must come after all hooks
  if (!currentActivity || !waysOfSeeingActivities?.length) {
    return null;
  }

  const styleName = mergedStyle?.name || currentActivity?.styleInfo?.name || 'Style';

  return (
    <div 
      className="bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
      onClick={() => {
        // Open the same details modal for both card click and view details button
        try { window.dispatchEvent(new Event('close-global-details-modal')); } catch (err) {}
        setShowDetailsModal(true);
      }}
    >
      {/* Subtle background removed (no emojis) */}
      <div className="absolute inset-0 opacity-0"></div>

      {/* Strip Label */}
      <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">
        Ways of Seeing
      </div>

      {/* Transformed Artwork Only */}
      <div className="relative mb-2 z-10 mt-3">
        <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
          <img 
            src={expandedImageUrl} 
            alt={`${currentActivity?.styleInfo?.name || 'Transformed'} artwork`} 
            className="w-full h-full object-cover"
            onLoad={() => {
              console.log('✅ Image loaded successfully:', expandedImageUrl);
            }}
            onError={(e) => {
              console.error('❌ Failed to load image:', expandedImageUrl);
              console.error('❌ Current activity:', currentActivity);
              e.target.style.display = 'none';
              // Show fallback content
              const fallback = e.target.parentElement.querySelector('.image-fallback');
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          {/* Fallback when image fails to load */}
          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
            <div className="text-center">
              <p className="text-sm text-orange-600 font-medium">
                {currentActivity?.styleInfo?.name || 'Transformed'} Artwork
              </p>
            </div>
          </div>
          <div className="absolute bottom-1 left-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 py-1 rounded text-xs font-bold shadow-md">
            {currentActivity?.styleInfo?.name || 'Transformed'}
          </div>
          
          {/* Navigation arrows on top right corner of artwork */}
          {waysOfSeeingActivities.length > 1 && (
            <div className="absolute top-1 right-1 flex gap-1 z-30">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                className="bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white p-1 rounded-full transition-all duration-300 shadow-md hover:scale-110"
              >
                <FaChevronLeft size={8} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white p-1 rounded-full transition-all duration-300 shadow-md hover:scale-110"
              >
                <FaChevronRight size={8} />
              </button>
            </div>
          )}
          
          {/* Delete button on bottom right of image thumbnail */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Call parent's delete handler directly - it will show confirmation modal
              if (onDeleteActivity && currentActivity?.id) {
                onDeleteActivity(currentActivity.id);
              }
            }}
            className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-30"
            title="Delete this creation"
          >
            <FaTrash size={8} />
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-1 relative z-10">
        <div className="text-center flex-1">
          <p className="text-xs text-orange-700 font-medium">
            {formatActivityDate(currentActivity)}
          </p>
        </div>
        
        {/* Page indicator for multiple transformations */}
        {waysOfSeeingActivities.length > 1 && (
          <div className="text-xs text-orange-600 font-medium">
            {currentIndex + 1}/{waysOfSeeingActivities.length}
          </div>
        )}
      </div>

      {/* View Details Button */}
      <div className="relative z-10 mt-1">
        <button 
          onClick={(e) => {
            e.stopPropagation();
            try { window.dispatchEvent(new Event('close-global-details-modal')); } catch (err) {}
            setShowDetailsModal(true);
          }}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
          style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
        >
          View details
        </button>
      </div>

      {/* Details Modal — premium Ways of Seeing shell */}
      <PremiumActivityModalShell
        open={showDetailsModal}
        onClose={closeDetailsModal}
        title={`Your Artwork in ${styleName} Style`}
        eyebrow="Magic Studio"
        subtitle="Ways of Seeing"
        maxWidthClass="max-w-[min(96vw,1152px)]"
        zIndexClass="z-[200120]"
        ariaLabelledBy="ways-of-seeing-scroller-modal-title"
      >
        <div className="bg-[#FFFDF9]/98 p-4 sm:p-6">
          {waysOfSeeingActivities.length > 1 && (
            <div className="mb-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:bg-white"
              >
                <FaChevronLeft size={14} />
              </button>
              <span className="rounded-full border border-[#F5D99B]/80 bg-[#FFF9EF] px-3 py-1 text-sm font-semibold text-[#92400E]">
                {currentIndex + 1} / {waysOfSeeingActivities.length}
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:bg-white"
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          )}

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Original */}
              <div className="original-result rounded-2xl border border-[#F5D99B]/60 bg-gradient-to-br from-[#FFFDF9] to-[#FFF7ED] p-4 shadow-[0_8px_24px_rgba(120,72,20,0.08)]">
                <h4 className={`mb-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7A55]`}>Original Artwork</h4>
                <div className="relative h-[min(52vh,400px)] w-full overflow-hidden rounded-2xl border border-[#F5D99B]/50 bg-[#FFF9EF]">
                  <img 
                    src={getFullImageUrl(originalArtworkUrl)} 
                    alt="Original artwork" 
                    className="w-full h-full object-cover" 
                    onError={(e) => {
                      const img = e.currentTarget;
                      const src = img.src;
                      const originalSrc = getFullImageUrl(originalArtworkUrl);
                      
                      console.error('❌ ReimagineScrollerCard: Failed to load original artwork:', {
                        originalSrc: originalSrc,
                        currentSrc: src,
                        isBlob: originalSrc?.startsWith('blob:'),
                        isDataUrl: originalSrc?.startsWith('data:image/')
                      });
                      
                      // Try proxy if it's a Firebase Storage URL
                      if (src && (src.includes('firebasestorage.googleapis.com') || src.includes('storage.googleapis.com'))) {
                        const backendUrl = getBackendBaseUrl();
                        const proxyUrl = `${backendUrl}/proxy-image?url=${encodeURIComponent(src)}`;
                        console.log('🔄 ReimagineScrollerCard: Retrying original artwork with proxy:', proxyUrl);
                        img.src = proxyUrl;
                        return;
                      }
                      
                      // For blob URLs, try to find a non-blob URL from the activities
                      if (originalSrc?.startsWith('blob:')) {
                        console.warn('⚠️ Blob URL failed, trying to find alternative from activities');
                        // The parent component should have resolved this, but if not, show fallback
                        img.style.display = 'none';
                        const fallback = img.parentElement.querySelector('.image-fallback');
                        if (fallback) fallback.style.display = 'flex';
                        return;
                      }
                      
                      // For data URLs, they should work, but if they fail, show fallback
                      if (originalSrc?.startsWith('data:image/')) {
                        console.warn('⚠️ Data URL failed to load, showing fallback');
                        img.style.display = 'none';
                        const fallback = img.parentElement.querySelector('.image-fallback');
                        if (fallback) fallback.style.display = 'flex';
                        return;
                      }
                      
                      img.style.display = 'none';
                      const fallback = img.parentElement.querySelector('.image-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                    onLoad={() => {
                      console.log('✅ ReimagineScrollerCard: Original artwork loaded successfully:', getFullImageUrl(originalArtworkUrl));
                    }}
                  />
                  {/* Fallback when image fails to load */}
                  <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                    <div className="text-center p-4">
                      <p className="text-sm text-amber-600 font-medium mb-2">Original Artwork</p>
                      <p className="text-xs text-amber-500">Unable to load image</p>
                    </div>
                  </div>
                  <div className="absolute bottom-2 left-2 rounded bg-white/90 px-2 py-1 text-sm font-semibold text-[#5C4428]">
                    Original
                  </div>
                </div>
              </div>

              {/* Transformed */}
              <div className="transformed-result rounded-2xl border border-[#F5D99B]/60 bg-gradient-to-br from-[#FFFDF9] to-[#FFF7ED] p-4 shadow-[0_8px_24px_rgba(120,72,20,0.08)]">
                <h4 className={`mb-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7A55]`}>{styleName} Style</h4>
                <div
                  className="group relative h-[min(52vh,400px)] w-full cursor-pointer overflow-hidden rounded-2xl border border-[#F5D99B]/50 bg-[#FFF9EF] transition hover:shadow-md"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openStyleLearnView(); }}
                  onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); openStyleLearnView(); }}
                >
                  <img src={expandedImageUrl} alt={`${styleName} transformed artwork`} className="h-full w-full object-cover" />
                  <div className="absolute bottom-2 left-2 rounded bg-white/90 px-2 py-1 text-sm font-semibold text-[#5C4428]">
                    {styleName}
                  </div>
                  <div className="absolute right-2 top-2 rounded-full bg-[#B45309] px-3 py-1 text-xs font-semibold text-white opacity-0 transition group-hover:opacity-100">
                    Double-click to expand
                  </div>
                </div>
                <p className={`mt-2 text-center text-sm ${coLearningInkMuted}`}>
                  Double-click to learn more about {styleName}!
                </p>
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openStyleLearnView(); }}
                    className="inline-flex items-center justify-center rounded-full border border-[#E8C97A] bg-gradient-to-r from-amber-500 to-orange-400 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-amber-600 hover:to-orange-500"
                  >
                    Learn About {styleName}
                  </button>
                </div>
              </div>
          </div>

          <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (onTutorialClick && currentActivity) {
                    closeDetailsModal();
                    onTutorialClick(currentActivity);
                  } else {
                    closeDetailsModal();
                    navigate('/art-world/selection', {
                      state: {
                        artwork: originalArtworkUrl,
                        fromPage: 'reimagine-scroller',
                        preSelectedStyle: currentActivity.styleInfo,
                        forceGenerate: true,
                      },
                    });
                  }
                }}
                className="inline-flex max-w-full items-center justify-center rounded-full border border-[#E8C97A] bg-gradient-to-r from-amber-600 via-amber-500 to-orange-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-amber-700 hover:to-orange-600"
              >
                {hasTutorial
                  ? `View ${styleName} Tutorial`
                  : `Let's learn how to make this ${styleName} artwork`}
              </button>
          </div>
        </div>
      </PremiumActivityModalShell>

      {/* Expanded style learn view — premium shell */}
      <PremiumActivityModalShell
        open={expanded && !!currentActivity}
        onClose={() => {
          setExpanded(false);
          setIsFadingExpanded(false);
          setShowDetailsModal(false);
          setIsFadingOut(false);
        }}
        title={`Let's learn about ${styleName}`}
        eyebrow="Magic Studio"
        subtitle="Ways of Seeing"
        maxWidthClass="max-w-[min(96vw,1200px)]"
        zIndexClass="z-[200130]"
        ariaLabelledBy="ways-of-seeing-expanded-modal-title"
      >
        <div className="bg-[#FFFDF9]/98 p-4 sm:p-6 lg:p-8">
          <div className="grid w-full min-w-0 max-w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,40%)_minmax(0,60%)] lg:items-start lg:gap-8">
            <h4 className={`mb-0 min-w-0 max-w-full text-lg font-semibold lg:col-start-1 lg:row-start-1 ${coLearningInkTitle}`}>
              Your Inspiration
            </h4>
            <div className="relative min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#F5D99B]/50 bg-[#FFF9EF] lg:col-start-1 lg:row-start-2 h-[min(52vh,500px)] w-full">
              <img
                src={getExpandedImage(currentActivity)}
                alt={`Artwork in ${styleName} style`}
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-3 left-3 rounded-lg bg-white/90 px-3 py-2 text-sm font-semibold text-[#5C4428] shadow">
                {styleName} Style
              </div>
            </div>
            <div className="mt-4 flex min-w-0 max-w-full justify-center px-1 lg:col-start-1 lg:row-start-3 lg:mt-4">
              <button
                type="button"
                onClick={() => {
                  if (onTutorialClick && currentActivity) {
                    onTutorialClick(currentActivity);
                    setExpanded(false);
                  }
                }}
                className="inline-flex max-w-full items-center justify-center rounded-full border border-[#E8C97A] bg-gradient-to-r from-amber-600 via-amber-500 to-orange-500 px-5 py-2.5 text-xs font-semibold text-white shadow-md transition hover:from-amber-700 sm:text-sm"
              >
                {`Let's learn how to make this ${styleName} artwork`}
              </button>
            </div>
            <div className="info-section box-border min-w-0 w-full max-w-full space-y-6 pr-0 sm:pr-1 lg:col-start-2 lg:row-span-2 lg:row-start-2 lg:min-h-0 lg:self-stretch">
              {scrollerAccordionStyleMeta && (
                <div className="description-section box-border min-w-0 w-full max-w-full">
                  <ArtformDetailAccordions
                    artformKey={currentActivity?.styleInfo?.id || ''}
                    styleMeta={scrollerAccordionStyleMeta}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </PremiumActivityModalShell>
    </div>
  );
};

export default ReimagineScrollerCard;
